import { OrderItem } from '@/types/order-list';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Dispatch, SetStateAction } from 'react';
import { TableBody, TableHeader } from './';

interface MarketTableProps<TData> {
  orderList: TData[];
  columns: ColumnDef<TData>[];
  columnOrder: string[];
  setColumnOrder: Dispatch<SetStateAction<string[]>>; // ✅ 수정
  checkedOrders?: OrderItem[];
  setCheckedOrders?: Dispatch<SetStateAction<OrderItem[]>>; // ✅ 수정
  pageIndex: number;
  pageSize: number;
  pageCount?: number;
}

const MarketTable = <TData extends object>({
  orderList,
  columns,
  columnOrder,
  setColumnOrder,
  checkedOrders,
  setCheckedOrders,
}: MarketTableProps<TData>) => {
  const isPreviewMode = useMembershipPreviewMode();

  const table = useReactTable({
    data: orderList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    state: {
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  return (
    <DndContext
      modifiers={[restrictToHorizontalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div
        className="scrollbar h-full overflow-auto"
        data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
      >
        <table className="w-full min-w-max overflow-hidden">
          <TableHeader table={table} columnOrder={columnOrder} />
          <TableBody
            table={table}
            checkedOrders={checkedOrders}
            setCheckedOrders={setCheckedOrders}
            columnOrder={columnOrder}
          />
        </table>
      </div>
    </DndContext>
  );
};

export default MarketTable;
